import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import Portal from '../components/Portal';
import '../components/Modal.css';
import './TiposPage.css';

const ICONES = ['📁','💳','📌','🗂','🏠','🚗','💊','🎓','🍽️','🛒','💡','📱','🎮','✈️','🐾','💈','🏋️','🎵'];

export default function TiposPage() {
  const { user, profile } = useAuth();
  const [tipos, setTipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editTipo, setEditTipo] = useState(null);
  const [msg, setMsg] = useState({ type: '', text: '' });

  const loadTipos = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('tipos_conta')
      .select('*')
      .eq('owner_id', user.id)
      .order('nome');
    setTipos(data || []);
    setLoading(false);
  }, [user.id]);

  useEffect(() => { loadTipos(); }, [loadTipos]);

  function flash(type, text) {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: '', text: '' }), 3000);
  }

  async function registrarAtividade(acao, descricao) {
    try {
      await supabase.from('historico_atividades').insert({
        owner_id: user.id,
        usuario_nome: profile?.name || 'Usuário',
        acao,
        descricao,
      });
    } catch (e) { /* silencioso */ }
  }

  async function handleSave(form) {
    if (editTipo) {
      await supabase.from('tipos_conta').update({ nome: form.nome, icone: form.icone }).eq('id', editTipo.id);
      await registrarAtividade('tipo_editado', `Tipo editado: ${form.icone} ${form.nome}`);
      flash('success', 'Tipo atualizado!');
    } else {
      await supabase.from('tipos_conta').insert({ nome: form.nome, icone: form.icone, owner_id: user.id });
      await registrarAtividade('tipo_criado', `Novo tipo criado: ${form.icone} ${form.nome}`);
      flash('success', 'Tipo criado!');
    }
    setShowModal(false);
    setEditTipo(null);
    await loadTipos();
  }

  async function handleDelete(tipo) {
    if (!window.confirm(`Excluir o tipo "${tipo.nome}"?`)) return;
    await supabase.from('tipos_conta').delete().eq('id', tipo.id);
    await registrarAtividade('tipo_excluido', `Tipo excluído: ${tipo.icone} ${tipo.nome}`);
    flash('success', 'Tipo excluído.');
    await loadTipos();
  }

  return (
    <div className="tipos-page fade-in">
      <div className="tipos-header">
        <div>
          <h2 className="tipos-title">Tipos de Conta</h2>
          <p className="text-muted text-sm">Crie categorias personalizadas para suas contas</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setEditTipo(null); setShowModal(true); }}>
          + Novo Tipo
        </button>
      </div>

      {msg.text && (
        <div className={`alert alert-${msg.type === 'error' ? 'error' : 'success'}`}>{msg.text}</div>
      )}

      {loading ? (
        <div style={{ display:'flex', justifyContent:'center', padding:'60px 0', gap:12, color:'var(--text2)' }}>
          <div className="spinner" style={{ width:28, height:28 }} />
          <span>Carregando...</span>
        </div>
      ) : (
        <>
          {tipos.length === 0 ? (
            <div className="tipos-empty">
              <p>Nenhum tipo cadastrado ainda.</p>
              <p className="text-muted text-sm">Clique em "+ Novo Tipo" para começar.</p>
            </div>
          ) : (
            <div className="tipos-grid">
              {tipos.map(t => (
                <div key={t.id} className="tipo-card">
                  <span className="tipo-icone">{t.icone}</span>
                  <span className="tipo-nome">{t.nome}</span>
                  <div className="tipo-acoes">
                    <button className="acao-btn edit" onClick={() => { setEditTipo(t); setShowModal(true); }} title="Editar">✎</button>
                    <button className="acao-btn del" onClick={() => handleDelete(t)} title="Excluir">✕</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {showModal && (
        <TipoModal
          tipo={editTipo}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditTipo(null); }}
        />
      )}
    </div>
  );
}

function TipoModal({ tipo, onSave, onClose }) {
  const [form, setForm] = useState({ nome: tipo?.nome || '', icone: tipo?.icone || '📁' });
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.nome.trim()) return;
    setSaving(true);
    await onSave(form);
    setSaving(false);
  }

  return (
    <Portal>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-box fade-in" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h3 className="modal-title">{tipo ? 'Editar Tipo' : 'Novo Tipo'}</h3>
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>
          <form onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label>Nome do Tipo</label>
              <input
                type="text"
                placeholder="Ex: Saúde, Educação, Lazer..."
                value={form.nome}
                onChange={e => setForm(p => ({ ...p, nome: e.target.value }))}
                autoFocus maxLength={40}
              />
            </div>
            <div className="form-group">
              <label>Ícone</label>
              <div className="icones-grid">
                {ICONES.map(ic => (
                  <button
                    key={ic}
                    type="button"
                    className={`icone-btn ${form.icone === ic ? 'active' : ''}`}
                    onClick={() => setForm(p => ({ ...p, icone: ic }))}
                  >
                    {ic}
                  </button>
                ))}
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-ghost" onClick={onClose}>Cancelar</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? <><span className="spinner" /> Salvando...</> : tipo ? 'Salvar' : 'Criar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Portal>
  );
}
